import React from 'react';
// Import ProposalResource from the correct path and remove local Resource interface
import { ProposalResource } from '../../hooks/useProposals';
import { useResourceOptions, Seniority } from '../../hooks/useResourceOptions';

// Local Resource interface removed, using ProposalResource from useProposals instead

interface ResourceInputProps {
    resources: ProposalResource[]; // Changed from Resource[]
    onChange: (resources: ProposalResource[]) => void; // Changed from Resource[]
    disabled?: boolean;
}

export default function ResourceInput({ resources, onChange, disabled = false }: ResourceInputProps) {
    const { types, getTechnologiesByType, seniorities, loading, error } = useResourceOptions();

    const addResource = () => {
        if (types.length === 0 || seniorities.length === 0) return;

        const firstType = types[0];
        const firstTech = getTechnologiesByType(firstType.id)[0];
        const firstSeniority = seniorities[0];

        onChange([
            ...resources,
            {
                quantity: 1,
                typeId: firstType.id,
                technologyId: firstTech?.id || '',
                seniority: firstSeniority.id,
            },
        ]);
    };

    const removeResource = (index: number) => {
        onChange(resources.filter((_, i) => i !== index));
    };

    // Updated field and value types to use ProposalResource
    const updateResource = (index: number, field: keyof ProposalResource, value: ProposalResource[keyof ProposalResource]) => {
        const newResources = [...resources];
        const resource = { ...newResources[index] };

        if (field === 'typeId') {
            const techs = getTechnologiesByType(value as string);
            resource.technologyId = techs[0]?.id || '';
        }

        // Type assertion for resource field assignment
        (resource[field] as ProposalResource[keyof ProposalResource]) = value;
        newResources[index] = resource;
        onChange(newResources);
    };

    if (loading) {
        return <div className="text-gray-500">Carregando opções...</div>;
    }

    if (error) {
        return <div className="text-red-500">{error}</div>;
    }

    if (types.length === 0 || seniorities.length === 0) {
        return (
            <div className="text-yellow-600 bg-yellow-50 p-4 rounded-md">
                Nenhum tipo de recurso ou senioridade cadastrado. Por favor, cadastre-os na seção administrativa.
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {resources.map((resource, index) => (
                <div key={index} className="flex items-end space-x-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Quantidade</label>
                        <input
                            type="number"
                            min="1"
                            value={resource.quantity}
                            onChange={(e) => updateResource(index, 'quantity', parseInt(e.target.value))}
                            className="input-primary mt-1 w-20"
                            disabled={disabled}
                        />
                    </div>
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">Tipo</label>
                        <select
                            value={resource.typeId}
                            onChange={(e) => updateResource(index, 'typeId', e.target.value)}
                            className="input-primary mt-1 w-full"
                            disabled={disabled}
                        >
                            {types.map((type) => (
                                <option key={type.id} value={type.id}>
                                    {type.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">Tecnologia</label>
                        <select
                            value={resource.technologyId}
                            onChange={(e) => updateResource(index, 'technologyId', e.target.value)}
                            className="input-primary mt-1 w-full"
                            disabled={disabled}
                        >
                            {getTechnologiesByType(resource.typeId).map((tech) => (
                                <option key={tech.id} value={tech.id}>
                                    {tech.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700">Senioridade</label>
                        <select
                            value={resource.seniority}
                            onChange={(e) => updateResource(index, 'seniority', e.target.value)}
                            className="input-primary mt-1 w-full"
                            disabled={disabled}
                        >
                            {seniorities.map((option: Seniority) => (
                                <option key={option.id} value={option.id}>
                                    {option.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button
                        type="button"
                        onClick={() => removeResource(index)}
                        className="btn-danger mb-1"
                        disabled={disabled}
                    >
                        Remover
                    </button>
                </div>
            ))}
            <button
                type="button"
                onClick={addResource}
                className="btn-secondary"
                disabled={disabled}
            >
                Adicionar Recurso
            </button>
        </div>
    );
} 